import { MarkerClusterer } from '@googlemaps/markerclusterer';
import { ScrollTrigger } from 'gsap/dist/ScrollTrigger';
import LocationSearch from './LocationSearch';

class GoogleMap {
  constructor(el) {
    this.$map = $(el);
    this.lat = this.$map.data('lat');
    this.lng = this.$map.data('lng');
    this.isGlobalMap = this.$map.hasClass('map__map');
    this.controlsPosition =
      this.$map.data('controls-position') || 'RIGHT_BOTTOM';
    // center is required
    if (!this.lat || !this.lng) return;

    // options
    this.defaultZoom = 8;
    this.zoom = this.$map.data('zoom') || this.defaultZoom;
    this.center = {
      lat: this.lat,
      lng: this.lng,
    };
    this.options = {
      zoom: this.zoom,
      mapTypeControl: false,
      streetViewControl: false,
      fullscreenControl: false,
      center: this.center,
      styles: [
        {
          featureType: 'all',
          elementType: 'labels.text.fill',
          stylers: [
            {
              saturation: 36,
            },
            {
              color: '#333333',
            },
            {
              lightness: 40,
            },
          ],
        },
        {
          featureType: 'all',
          elementType: 'labels.text.stroke',
          stylers: [
            {
              visibility: 'on',
            },
            {
              color: '#ffffff',
            },
            {
              lightness: 16,
            },
          ],
        },
        {
          featureType: 'all',
          elementType: 'labels.icon',
          stylers: [
            {
              visibility: 'off',
            },
          ],
        },
        {
          featureType: 'administrative',
          elementType: 'geometry.fill',
          stylers: [
            {
              color: '#fefefe',
            },
            {
              lightness: 20,
            },
          ],
        },
        {
          featureType: 'administrative',
          elementType: 'geometry.stroke',
          stylers: [
            {
              color: '#fefefe',
            },
            {
              lightness: 17,
            },
            {
              weight: 1.2,
            },
          ],
        },
        {
          featureType: 'landscape',
          elementType: 'geometry',
          stylers: [
            {
              color: '#ededed',
            },
            {
              lightness: 20,
            },
          ],
        },
        {
          featureType: 'poi',
          elementType: 'geometry',
          stylers: [
            {
              color: '#ededed',
            },
            {
              lightness: 21,
            },
          ],
        },
        {
          featureType: 'poi.park',
          elementType: 'geometry',
          stylers: [
            {
              color: '#dedede',
            },
            {
              lightness: 21,
            },
            {
              visibility: 'off',
            },
          ],
        },
        {
          featureType: 'poi.park',
          elementType: 'labels',
          stylers: [
            {
              visibility: 'off',
            },
          ],
        },
        {
          featureType: 'road.highway',
          elementType: 'geometry.fill',
          stylers: [
            {
              color: '#ffffff',
            },
            {
              lightness: 17,
            },
          ],
        },
        {
          featureType: 'road.highway',
          elementType: 'geometry.stroke',
          stylers: [
            {
              color: '#ffffff',
            },
            {
              lightness: 29,
            },
            {
              weight: 0.2,
            },
          ],
        },
        {
          featureType: 'road.arterial',
          elementType: 'geometry',
          stylers: [
            {
              color: '#ffffff',
            },
            {
              lightness: 18,
            },
          ],
        },
        {
          featureType: 'road.local',
          elementType: 'geometry',
          stylers: [
            {
              color: '#ffffff',
            },
            {
              lightness: 16,
            },
          ],
        },
        {
          featureType: 'transit',
          elementType: 'geometry',
          stylers: [
            {
              color: '#f2f2f2',
            },
            {
              lightness: 19,
            },
          ],
        },
        {
          featureType: 'transit.station.rail',
          elementType: 'labels.text',
          stylers: [
            {
              visibility: 'on',
            },
          ],
        },
        {
          featureType: 'transit.station.rail',
          elementType: 'labels.text.fill',
          stylers: [
            {
              visibility: 'on',
            },
          ],
        },
        {
          featureType: 'transit.station.rail',
          elementType: 'labels.text.stroke',
          stylers: [
            {
              visibility: 'on',
            },
          ],
        },
        {
          featureType: 'transit.station.rail',
          elementType: 'labels.icon',
          stylers: [
            {
              visibility: 'on',
            },
          ],
        },
        {
          featureType: 'water',
          elementType: 'geometry',
          stylers: [
            {
              color: '#dcdcdc',
            },
            {
              lightness: 17,
            },
          ],
        },
      ],
    };

    // markers
    this.$markers = this.isGlobalMap
      ? $('.map__restaurants .restaurant-card')
      : this.$map.find('.marker');
    this.markers = [];
    this.infoWindow = null;

    this.init();

    if (this.isGlobalMap) {
      this.$container = $('.map');
      this.$inner = this.$container.find('.map__inner');
      this.$closest = this.$container.find('.map__closest');
      this.$closestList = this.$closest.find('.map__closest-list');
      this.$closestTitle = this.$closest.find('.map__area span');

      this.initHeaderNav();
      this.initSearch();
    }
  }

  async init() {
    await this.initMap();
    await this.addMarkers();

    // check if url has ?location= query param
    const urlParams = new URLSearchParams(window.location.search);
    const location = urlParams.get('location');
    if (location) {
      this.onSearch(location);
    }
  }

  async initMap() {
    const { Map } = await google.maps.importLibrary('maps');

    this.options.mapTypeId = google.maps.MapTypeId.ROADMAP;
    this.options.zoomControlOptions = {
      position: google.maps.ControlPosition[this.controlsPosition],
    };

    this.map = new Map(this.$map[0], this.options);

    // create geocoder for global map
    if (this.isGlobalMap) {
      await this.initGeocoder();
    }
  }

  async initGeocoder() {
    const { Geocoder } = await google.maps.importLibrary('geocoding');
    const { spherical } = await google.maps.importLibrary('geometry');

    this.geocoder = new Geocoder();
    this.spherical = spherical;
  }

  async addMarkers() {
    const { Marker } = await google.maps.importLibrary('marker');
    const { InfoWindow } = await google.maps.importLibrary('maps');

    // info window
    this.infoWindow = new InfoWindow({
      content: '',
      ariaLabel: '',
    });

    // add center marker only if center title exists
    this.$markers.each((i, el) => {
      const $el = $(el);
      const lat = $el.data('lat');
      const lng = $el.data('lng');
      const title = $el.data('title');
      const isComingSoon = $el.hasClass('restaurant-card--coming-soon');

      // position and title required
      if (!lat || !lng || !title) {
        return;
      }

      const marker = new Marker({
        map: this.map,
        position: {
          lat,
          lng,
        },
        title,
        icon: {
          path: 'M16.9915 21.2473C18.135 21.2473 19.1138 20.8402 19.9281 20.0259C20.7424 19.2116 21.1495 18.2328 21.1495 17.0893C21.1495 15.9458 20.7424 14.967 19.9281 14.1527C19.1138 13.3384 18.135 12.9313 16.9915 12.9313C15.8481 12.9313 14.8692 13.3384 14.0549 14.1527C13.2406 14.967 12.8335 15.9458 12.8335 17.0893C12.8335 18.2328 13.2406 19.2116 14.0549 20.0259C14.8692 20.8402 15.8481 21.2473 16.9915 21.2473ZM16.9915 42.0375C11.4128 37.2904 7.24612 32.8812 4.49142 28.8098C1.73672 24.7383 0.359375 20.9701 0.359375 17.5051C0.359375 12.3076 2.03125 8.16685 5.375 5.08297C8.71876 1.9991 12.5909 0.457157 16.9915 0.457157C21.3921 0.457157 25.2643 1.9991 28.608 5.08297C31.9518 8.16685 33.6236 12.3076 33.6236 17.5051C33.6236 20.9701 32.2463 24.7383 29.4916 28.8098C26.7369 32.8812 22.5702 37.2904 16.9915 42.0375Z',
          fillColor: isComingSoon ? '#5a5c5e' : '#E02B28',
          fillOpacity: 1,
          strokeWeight: 0,
          size: new google.maps.Size(34, 43),
          anchor: new google.maps.Point(17, 43),
        },
      });
      this.markers.push(marker);

      // we need info window only on global map
      if (!this.isGlobalMap) return;

      // clone element and remove reveal-slide class
      const infoWindowContent = $el.clone().removeClass('reveal-slide').get(0);

      // open info window on marker click
      marker.addListener('click', () => {
        this.infoWindow.setContent(infoWindowContent);
        this.infoWindow.open({
          anchor: marker,
          map: this.map,
        });
      });
    });

    // set up marker cluster
    this.markerCluster = new MarkerClusterer({
      map: this.map,
      markers: this.markers,
      renderer: {
        render({ count, position }) {
          const color = '#e02b28';

          // create svg literal with fill color
          const svg = `<svg fill="${color}" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 240 240" width="50" height="50">
                        <circle cx="120" cy="120" r="70" />
                        <circle cx="120" cy="120" opacity=".5" r="90" />
                        <circle cx="120" cy="120" opacity=".2" r="110" />
                        <text x="50%" y="50%" style="fill:#fff" text-anchor="middle" font-size="50" dominant-baseline="middle" font-family="roboto,arial,sans-serif">${count}</text>
                      </svg>`;

          const title = `Cluster of ${count} markers`;

          // adjust zIndex to be above other markers
          const zIndex = Number(google.maps.Marker.MAX_ZINDEX) + count;

          const clusterOptions = {
            position,
            zIndex,
            title,
            icon: {
              url: `data:image/svg+xml;base64,${btoa(svg)}`,
              anchor: new google.maps.Point(25, 25),
            },
          };
          return new Marker(clusterOptions);
        },
      },
    });

    // center map on restaurant card click
    if (this.isGlobalMap) {
      this.$markers.on('click', (e) => {
        const $el = $(e.currentTarget);
        const title = $el.data('title');
        const lat = $el.data('lat');
        const lng = $el.data('lng');

        this.selectPizzeria({ title, lat, lng });
      });
    }
  }

  initHeaderNav() {
    this.mobileView = 'list'; // list or map
    this.$headerBtns = $('.header__map-btn');

    this.$headerBtns.on('click', (e) => {
      const $el = $(e.currentTarget);
      const view = $el.data('view');

      // update view
      if (view === this.mobileView) return;
      this.mobileView = view;

      this.changeMobileView();
    });
  }

  initSearch() {
    // mobile search
    this.$mobileSearch = this.$container.find(
      '.map__search-wrap--mobile .search'
    );
    this.mobileSearch = new LocationSearch(this.$mobileSearch, {
      onSearch: this.onSearch.bind(this),
      onClosest: this.onClosest.bind(this),
      onOptionClick: this.onOptionClick.bind(this),
      onChange: this.onLocationChange.bind(this),
    });

    // pin desktop search
    this.$desktopSearchWrap = this.$container.find(
      '.map__search-wrap--desktop'
    );
    const $last = this.$inner.children().last();
    ScrollTrigger.create({
      trigger: this.$desktopSearchWrap[0],
      start: 'top top',
      endTrigger: $last[0],
      end: 'bottom top',
      pin: true,
      scroller: this.$inner[0],
      pinSpacing: false,
    });

    // desktop search
    this.$desktopSearch = this.$desktopSearchWrap.find('.search');
    this.desktopSearch = new LocationSearch(this.$desktopSearch, {
      onSearch: this.onSearch.bind(this),
      onClosest: this.onClosest.bind(this),
      onOptionClick: this.onOptionClick.bind(this),
      onChange: this.onLocationChange.bind(this),
    });
  }

  // update container
  changeMobileView() {
    this.$container.removeClass('is-list-view is-map-view');
    this.$container.addClass(`is-${this.mobileView}-view`);

    // update classes
    this.$headerBtns.removeClass('is-active');
    this.$headerBtns
      .filter(`[data-view="${this.mobileView}"]`)
      .addClass('is-active');
  }

  selectPizzeria({ lat, lng, title }) {
    this.map.setCenter({
      lat,
      lng,
    });

    // find marker by Title
    const marker = this.markers.find((m) => m.getTitle() === title);
    google.maps.event.trigger(marker, 'click');

    this.mobileView = 'map';
    this.changeMobileView();
  }

  async onSearch(value) {
    this.currentSearchLocation = value;

    const geocoderRequest = {
      address: this.currentSearchLocation,
      region: 'GB',
    };

    try {
      const { results } = await this.geocoder.geocode(geocoderRequest);
      const firstResult = results[0].geometry.location;
      this.map.setCenter(firstResult);
      this.map.setZoom(10);

      // find closest marker
      this.closestMarkers = this.markers
        .map((marker) => {
          const distance = this.spherical.computeDistanceBetween(
            marker.getPosition(),
            firstResult
          );
          return {
            title: marker.getTitle(),
            distance,
          };
        })
        .sort((a, b) => a.distance - b.distance)
        .slice(0, 3);

      const closestElements = this.closestMarkers.map((marker) => {
        const $el = this.$markers.filter(`[data-title="${marker.title}"]`);
        return $el.clone().removeClass('reveal-slide').get(0);
      });

      this.$closestTitle.text(this.currentSearchLocation);
      this.$closestList.html(closestElements);
      this.mobileSearch.setValue(this.currentSearchLocation);
      this.desktopSearch.setValue(this.currentSearchLocation);
      this.$closest.slideDown();
      this.addSearchLocationMarker(firstResult);

      // add location query param
      const url = new URL(window.location.href);
      url.searchParams.set('location', this.currentSearchLocation);
      window.history.pushState({}, '', url);

      // update mobile view
      this.mobileView = 'map';
      this.changeMobileView();
    } catch (error) {
      this.mobileSearch.noResultsFound();
      this.desktopSearch.noResultsFound();
    }
  }

  async addSearchLocationMarker(location) {
    const { Marker } = await google.maps.importLibrary('marker');

    this.searchMarker = new Marker({
      map: this.map,
      position: location,
      title: this.currentSearchLocation,
      icon: {
        path: google.maps.SymbolPath.CIRCLE,
        fillColor: '#5A5C5E',
        fillOpacity: 1,
        strokeWeight: 2,
        strokeColor: '#fff',
        strokeOpacity: 1,
        scale: 8.5,
      },
    });
  }

  onOptionClick(pizzeria) {
    const { title, lat, lng } = pizzeria;
    this.selectPizzeria({ title, lat, lng });
  }

  onClosest(closest) {
    const { title, lat, lng } = closest;
    this.selectPizzeria({ title, lat, lng });
    this.clearSearch();
  }

  onLocationChange(value) {
    if (value !== this.currentSearchLocation) {
      this.clearSearch();
    }
  }

  clearSearch() {
    this.$closest.slideUp();
    this.currentSearchLocation = null;

    if (this.searchMarker) {
      this.searchMarker.setMap(null);
    }

    // remove location query param
    const url = new URL(window.location.href);
    url.searchParams.delete('location');
    window.history.pushState({}, '', url);
  }
}

export default GoogleMap;
